import { useMemo } from 'react';
import { Field, useFormikContext } from 'formik';
import { Chassis, useFetchChassisQuery } from '@api/Trinium/Endpoints/Chassis/slice';
import { FormikAutocomplete } from '@forms/Components/AutocompleteInput';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { createSelector } from '@reduxjs/toolkit';
import { CheckInput } from '@forms/Components/CheckInput';

import styles from '../Components/Form.module.css';
import { formatEquipmentValue } from './EquipmentField';

interface ChassisFieldProps {
  fieldName: string;
  className?: string;
}

/**
 * Chassis Autocomplete Formik Input, used by the Research form
 */
export function ChassisField({ fieldName, className }: ChassisFieldProps) {
  const { number: terminalNumber } = useCurrentTerminal();
  const { getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<string>(fieldName);

  const { data: chassisData } = useFetchChassisQuery({ terminalNumber });
  const chassisItems = useMemo(() => {
    return chassisData ? chassisData.map((chassis) => chassis.id) : value ? [value] : [];
  }, [chassisData, value]);

  return (
    <Field
      name={fieldName}
      label='Chassis #'
      placeholder='Enter Chassis Number'
      component={FormikAutocomplete}
      autoCompleteItems={chassisItems}
      className={className}
      freeSolo
    />
  );
}

interface OwnedChassisFieldProps {
  fieldName: string;
  className?: string;
  checkboxClassName?: string;
  disabled?: boolean;
  disabledReason?: string;
}

export function OwnedChassisField(props: OwnedChassisFieldProps) {
  const { getFieldMeta, setFieldValue } = useFormikContext();
  const { value } = getFieldMeta(props.fieldName);

  const { number: terminalNumber } = useCurrentTerminal();
  const selectFilteredChassis = useMemo(() => {
    const noChassis: Chassis[] = [];

    return createSelector(
      (chassis: Chassis[] | null | undefined) => chassis,
      (_: Chassis[] | null | undefined, searchInput: string | null | undefined) => searchInput,
      (chassis, searchInput) => {
        if (!chassis) {
          return noChassis;
        }

        if (!searchInput) {
          return chassis;
        }

        return chassis.filter((chs) => chs.id.toLowerCase().includes(searchInput.toLowerCase())).slice(0, 50);
      }
    );
  }, []);

  const { filteredChassis, data: chassisList } = useFetchChassisQuery(
    { terminalNumber, status: 'Available' },
    {
      skip: !value,
      selectFromResult: ({ data, isFetching }) => ({
        filteredChassis: selectFilteredChassis(data, String(value)),
        data,
        isFetching
      })
    }
  );

  const isChassisOwned = useMemo(() => {
    if (chassisList) {
      return Boolean(chassisList.find((x) => x.id === value));
    }
    return false;
  }, [chassisList, value]);

  return (
    <>
      <Field
        name={props.fieldName}
        label='Chassis'
        component={FormikAutocomplete}
        className={props.className}
        autoCompleteItems={filteredChassis.map((chassis) => chassis.id)}
        freeSolo
        onBlurExtension={(event: React.FocusEvent<HTMLInputElement>) => {
          const formattedValue = formatEquipmentValue(event.target.value);
          void setFieldValue(props.fieldName, formattedValue);
        }}
        disabled={props.disabled}
        disabledReason={props.disabled ? props.disabledReason : undefined}
      />

      <CheckInput
        fieldName='isChassisOwned'
        value={isChassisOwned}
        label='Is Owned'
        className={props.checkboxClassName}
        isDisabled
        disabledReason='Field is not editable'
        disabledTooltipClass={styles['chk-center']}
      />
    </>
  );
}
